// SIP password generation (#890) — CSPRNG-backed, replaces Math.random().toString(36).
// SIP registration passwords gate PSTN dial-out; predictable credentials = toll fraud.
// Pure module so it is unit-testable under node:test (see sipPassword.test.ts).

// Mixed alphabet, ambiguous glyphs (0/O, 1/l/I) excluded so passwords survive
// being read aloud or typed into a phone keypad config screen.
export const SIP_PASSWORD_ALPHABET =
  'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789';

export const SIP_PASSWORD_LENGTH = 16;

// Injectable for tests; browsers and Node >= 19 both expose globalThis.crypto.
type RandomSource = { getRandomValues: (buf: Uint8Array) => Uint8Array };

export function generateSipPassword(
  length: number = SIP_PASSWORD_LENGTH,
  cryptoObj: RandomSource | undefined = (globalThis as any).crypto,
): string {
  if (!cryptoObj || typeof cryptoObj.getRandomValues !== 'function') {
    // Never silently fall back to Math.random — fail loudly instead.
    throw new Error('crypto.getRandomValues unavailable; cannot generate SIP password');
  }
  const alphabet = SIP_PASSWORD_ALPHABET;
  // Rejection sampling: discard bytes >= the largest multiple of alphabet.length
  // so every character is uniformly likely (256 % 57 !== 0).
  const limit = 256 - (256 % alphabet.length);
  let out = '';
  const buf = new Uint8Array(length * 2);
  while (out.length < length) {
    cryptoObj.getRandomValues(buf);
    for (const byte of buf) {
      if (byte < limit) {
        out += alphabet[byte % alphabet.length];
        if (out.length === length) break;
      }
    }
  }
  return out;
}
